/**
 * Focused optimizer breakdowns — the Run Diagnostic / Run Pacing /
 * Run Performance results.
 *
 * Each focused launcher answers one question and emits its own shape
 * (campaign-services/campaign_optimizer/*_optimizer_launcher.py). The
 * generic optimization view only rendered their recommendation lists as cards;
 * the findings that justify the cards — IO pacing math, per-line delivery
 * diagnosis, line-item performance reads — had no renderer at all.
 *
 * Four renderer families, because four groups of platforms emit four shapes:
 * `dv360`, `gads` (Search and Performance Max), `social` (Facebook and LinkedIn)
 * and `yahoo`. See findingsFamily below for how a result is routed to one.
 *
 * Mirrors the Crimtan breakdown's shape (its Section/Badge/card primitives are
 * imported, not copied):
 *
 *   1. FINDINGS        <FocusedFindingsSection>   — what the analysis found
 *   2. RECOMMENDATION  (the existing suggestion cards, with their Apply buttons)
 *   3. VERDICT         <FocusedVerdictSection>    — the closing read + extras
 *
 * The cards in the middle are deliberately untouched: they own the
 * apply/applied state keyed on the backend's original suggestion index.
 */

import CampaignHealthReadout from './CampaignHealthReadout';
import { Badge, Section, cardStyle, IdChip, text } from './CrimtanOptimizationBreakdown';

/** Activity-header titles per focused analysis kind. */
export const FOCUSED_ANALYSIS_LABELS = {
  diagnostic: 'Delivery Diagnostic',
  pacing: 'Pacing Analysis',
  performance: 'Performance Analysis',
  // The summary run has no findings/verdict renderer here and never reaches one — it
  // returns a page it laid out itself, and CampaignActivity branches to
  // SummaryOptimizerLayout before the focused sections below. It is in this map only so
  // the activity row is titled while it runs and once it lands.
  summary: 'Campaign Summary',
};

/**
 * The focused analysis kind of a result, or null for everything else. The
 * launcher base stamps its PROMPT_KIND onto the result envelope as
 * `action_type` (dv360_optimizer_launcher_base.py), so the result itself says
 * which button produced it — the message row never carries it.
 */
export function focusedAnalysisKind(jsonResponse) {
  const kind = String(jsonResponse?.action_type || '').toLowerCase();
  return FOCUSED_ANALYSIS_LABELS[kind] ? kind : null;
}

/**
 * The focused kind of an activity MESSAGE. A completed run says it on the
 * result envelope; a still-processing run has no envelope yet, but chat.js
 * stamps the kind onto the message's sub_action at dispatch — so the row is
 * titled correctly the whole time it runs, not just once it finishes.
 */
export function focusedAnalysisKindFromMessage(message) {
  const fromResult = focusedAnalysisKind(message?.json_response);
  if (fromResult) return fromResult;
  const sub = String(message?.sub_action || '').toLowerCase();
  return FOCUSED_ANALYSIS_LABELS[sub] ? sub : null;
}

const summaryBox = {
  padding: '12px 15px', background: '#fffbeb', border: '1px solid #fde68a',
  borderRadius: '6px', marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
};

const quietBox = {
  padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px',
  marginBottom: '8px', fontSize: '0.88em', lineHeight: 1.5,
};

const SummaryBox = ({ label, children }) => (
  <div style={summaryBox}>
    {label && <strong style={{ display: 'block', marginBottom: '4px' }}>{label}</strong>}
    {children}
  </div>
);

const RowTitle = ({ name, id, badgeLevel, badgeText }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '6px' }}>
    {name && <strong style={{ fontSize: '0.9em' }}>{name}</strong>}
    {id && <IdChip>{id}</IdChip>}
    {badgeText && <Badge level={badgeLevel}>{badgeText}</Badge>}
  </div>
);

const Detail = ({ children }) => (
  <p style={{ margin: 0, fontSize: '0.85em', color: '#444' }}>{children}</p>
);

const num = (v) => (typeof v === 'number' ? v.toLocaleString() : text(v));

/* ------------------------------------------------------------------ */
/* Pacing                                                              */
/* ------------------------------------------------------------------ */

function PacingFindings({ details }) {
  const summary = text(details.pacing_summary);
  const ios = (Array.isArray(details.insertion_order_pacing) ? details.insertion_order_pacing : []).filter(Boolean);
  const contributors = (Array.isArray(details.shortfall_contributors) ? details.shortfall_contributors : []).filter(Boolean);
  const excluded = (Array.isArray(details.excluded_from_pacing) ? details.excluded_from_pacing : []).filter(Boolean);
  if (!summary && !ios.length && !contributors.length) return null;
  return (
    <Section step="1" title="Pacing" subtitle="Each insertion order against its booked goal.">
      {summary && <SummaryBox label="Pacing">{summary}</SummaryBox>}
      {ios.map((io, i) => (
        <div key={`${text(io.insertion_order_id) || 'io'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(io.insertion_order_name)}
            id={text(io.insertion_order_id)}
            badgeLevel={io.pacing_status}
            badgeText={text(io.pacing_status)?.replace(/_/g, ' ')}
          />
          <Detail>
            Delivered {num(io.delivered)} of {num(io.current_segment?.goal ?? io.flight_total_goal)}
            {io.days_remaining != null && <> · {num(io.days_remaining)} days left</>}
            {io.required_daily_rate != null && <> · needs {num(io.required_daily_rate)}/day vs {num(io.recent_daily_rate)}/day now</>}
            {io.projected_final_delivery != null && <> · projected {num(io.projected_final_delivery)}</>}
          </Detail>
          {/* A NOT_COMPUTED row without its reason is a dead end: the reason IS the
              finding (the segment has not started, the report does not reach back to
              it, the IO is booked in currency and the rollup carries impressions).
              The pacing schema puts it in `math` when the status is NOT_COMPUTED, and
              the card used to show the badge alone — three live insertion orders read
              as "NOT COMPUTED" with no way to tell why from this screen. */}
          {String(io.pacing_status || '').toUpperCase() === 'NOT_COMPUTED'
            && text(io.pacing_status_reason || io.math) && (
            <Detail>
              <span style={{ color: '#6b7280' }}>{text(io.pacing_status_reason || io.math)}</span>
            </Detail>
          )}
          {text(io.binding_constraint) && !['none', 'n/a'].includes(text(io.binding_constraint).toLowerCase()) && (
            <div style={{ marginTop: '6px' }}>
              <Badge level="medium">{text(io.binding_constraint).replace(/_/g, ' ')}</Badge>
            </div>
          )}
        </div>
      ))}
      {contributors.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Where the shortfall comes from</h6>
          {contributors.map((c, i) => (
            <div key={`${text(c.line_item_id) || 'li'}-${i}`} style={cardStyle}>
              <RowTitle
                name={text(c.line_item_name)}
                id={text(c.line_item_id)}
                badgeLevel="medium"
                badgeText={text(c.cause)?.replace(/_/g, ' ')}
              />
              {text(c.detail) && <Detail>{text(c.detail)}</Detail>}
            </div>
          ))}
        </div>
      )}
      {excluded.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: '0.8em', color: '#6b7280' }}>
          Excluded from pacing: {excluded.map((e) => text(e.line_item_id)).filter(Boolean).join(', ')} (handled by the diagnostic run)
        </p>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Diagnostic                                                          */
/* ------------------------------------------------------------------ */

function DiagnosticFindings({ details }) {
  const health = (details.campaign_health && typeof details.campaign_health === 'object') ? details.campaign_health : {};
  const diagnosis = (Array.isArray(details.delivery_diagnosis) ? details.delivery_diagnosis : []).filter(Boolean);
  const reactivations = (Array.isArray(details.reactivation_candidates) ? details.reactivation_candidates : []).filter(Boolean);
  const blocked = diagnosis.filter((d) => d.can_deliver === false || String(d.can_deliver).toLowerCase() === 'false');
  // One row per insertion order, deduplicated on the IO by the launcher's schema. An IO
  // where every line is blocked is the finding a reader must not have to assemble from a
  // column of line-item cards.
  const ios = (Array.isArray(details.insertion_order_status) ? details.insertion_order_status : []).filter(Boolean);
  if (!text(health.summary) && !diagnosis.length && !ios.length) return null;
  const verdictLevel = { HEALTHY: 'ok', DEGRADED: 'medium', STALLED: 'blocked' }[String(health.verdict || '').toUpperCase()];
  const ioLevel = { DELIVERING: 'ok', PARTIALLY_BLOCKED: 'medium', STALLED: 'blocked' };
  return (
    <Section step="1" title="Diagnosis" subtitle="Whether each line item can actually deliver.">
      {(text(health.verdict) || text(health.summary)) && (
        <SummaryBox label="Campaign health">
          {text(health.verdict) && (
            <span style={{ marginRight: '8px' }}>
              <Badge level={verdictLevel}>{text(health.verdict)}</Badge>
            </span>
          )}
          {text(health.summary)}
          {health.line_items_blocked != null && (
            <span style={{ color: '#6b7280' }}> ({num(health.line_items_blocked)} of {num(health.line_items_checked)} line items blocked)</span>
          )}
        </SummaryBox>
      )}
      {ios.map((io, i) => (
        <div key={`${text(io.insertion_order_id) || 'io'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(io.insertion_order_name)}
            id={text(io.insertion_order_id)}
            badgeLevel={ioLevel[String(io.status || '').toUpperCase()]}
            badgeText={text(io.status)?.replace(/_/g, ' ')}
          />
          <Detail>
            {io.blocked_line_items != null && <>{num(io.blocked_line_items)} of {num(io.total_line_items)} line items blocked</>}
            {io.impressions_in_report != null && <> · {num(io.impressions_in_report)} impressions</>}
            {text(io.top_blocker) && !['none', 'n/a'].includes(text(io.top_blocker).toLowerCase()) && (
              <> · mostly {text(io.top_blocker).replace(/_/g, ' ').toLowerCase()}</>
            )}
          </Detail>
          {text(io.summary) && <Detail>{text(io.summary)}</Detail>}
        </div>
      ))}
      {blocked.map((d, i) => (
        <div key={`${text(d.line_item_id) || 'li'}-${i}`} style={cardStyle}>
          <RowTitle name={text(d.line_item_name)} id={text(d.line_item_id)} badgeLevel="blocked" badgeText="cannot deliver" />
          {(Array.isArray(d.blockers) ? d.blockers : []).map(text).filter((b) => b && b.toLowerCase() !== 'none').length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {d.blockers.map(text).filter((b) => b && b.toLowerCase() !== 'none').map((b, bi) => (
                <Badge key={bi} level="high">{b.replace(/_/g, ' ')}</Badge>
              ))}
            </div>
          )}
          {text(d.fix) && <Detail>Fix: {text(d.fix)}</Detail>}
          {text(d.evidence) && <Detail>{text(d.evidence)}</Detail>}
        </div>
      ))}
      {/* blocking_issues is deliberately NOT rendered here. Each one is a job — an
          insertion order, the line items it covers, and the fix — so it belongs in the
          recommendations table below, where it gets columns and an action cell. Rendered
          in both places it was the same blocker printed twice on one page. */}
      {reactivations.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Reactivation candidates</h6>
          {reactivations.map((r, i) => (
            <div key={`${text(r.line_item_id) || 'li'}-${i}`} style={cardStyle}>
              <RowTitle name={text(r.line_item_name)} id={text(r.line_item_id)} />
              {text(r.rationale) && <Detail>{text(r.rationale)}</Detail>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Performance                                                         */
/* ------------------------------------------------------------------ */

function PerformanceFindings({ details }) {
  const summary = (details.performance_summary && typeof details.performance_summary === 'object')
    ? details.performance_summary : {};
  const lines = (Array.isArray(details.line_item_performance) ? details.line_item_performance : []).filter(Boolean);
  // One row per insertion order, deduplicated on the IO by the launcher's schema. The
  // line items under one IO share a goal and a budget, so the IO is the level at which
  // "is this working?" is answered — a reader should not have to add up line-item cards
  // to find the insertion order carrying the campaign.
  const ios = (Array.isArray(details.insertion_order_performance) ? details.insertion_order_performance : []).filter(Boolean);
  if (!text(summary.overall) && !lines.length && !ios.length) return null;
  const assessLevel = {
    STRONG: 'ok', ADEQUATE: 'low', UNDERPERFORMING: 'blocked', UNDER_SCALED: 'medium',
  };
  return (
    <Section step="1" title="Performance" subtitle="How each line item performs against its peers.">
      {text(summary.overall) && <SummaryBox label="Performance">{text(summary.overall)}</SummaryBox>}
      {text(summary.concentration_risk) && <div style={quietBox}>{text(summary.concentration_risk)}</div>}
      {ios.map((io, i) => (
        <div key={`${text(io.insertion_order_id) || 'io'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(io.insertion_order_name)}
            id={text(io.insertion_order_id)}
            badgeLevel={assessLevel[String(io.assessment || '').toUpperCase()]}
            badgeText={text(io.assessment)?.replace(/_/g, ' ')}
          />
          <Detail>
            {io.line_items_assessed != null && <>{num(io.line_items_assessed)} line items</>}
            {io.impressions != null && <> · {num(io.impressions)} impressions</>}
            {text(io.top_line_item?.line_item_name) && (
              <> · led by {text(io.top_line_item.line_item_name)}
                {text(io.top_line_item.share_of_io_delivery) && <> ({text(io.top_line_item.share_of_io_delivery)})</>}
              </>
            )}
          </Detail>
          {text(io.efficiency_read) && <Detail>{text(io.efficiency_read)}</Detail>}
          {text(io.concentration_risk) && <Detail>{text(io.concentration_risk)}</Detail>}
          {text(io.biggest_opportunity) && (
            <Detail><strong>Biggest opportunity:</strong> {text(io.biggest_opportunity)}</Detail>
          )}
        </div>
      ))}
      {lines.map((li, i) => (
        <div key={`${text(li.line_item_id) || 'li'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(li.line_item_name)}
            id={text(li.line_item_id)}
            badgeLevel={assessLevel[String(li.assessment || '').toUpperCase()]}
            badgeText={text(li.assessment)?.replace(/_/g, ' ')}
          />
          <Detail>
            {text(li.tactic)}
            {li.impressions != null && <> · {num(li.impressions)} impressions</>}
            {text(li.share_of_io_delivery) && <> · {text(li.share_of_io_delivery)} of IO delivery</>}
          </Detail>
          {text(li.efficiency_read) && <Detail>{text(li.efficiency_read)}</Detail>}
        </div>
      ))}
    </Section>
  );
}

/** Performance extras rendered after the cards: audiences, PMP deals, new lines. */
function PerformanceExtras({ details }) {
  const audiences = (Array.isArray(details.audience_recommendations) ? details.audience_recommendations : []).filter(Boolean);
  const pmp = (Array.isArray(details.pmp_recommendations) ? details.pmp_recommendations : []).filter(Boolean);
  const newLines = (Array.isArray(details.new_line_item_proposals) ? details.new_line_item_proposals : []).filter(Boolean);
  if (!audiences.length && !pmp.length && !newLines.length) return null;
  return (
    <Section step="3" title="Expansions" subtitle="Audiences, deals and new line items to add.">
      {audiences.map((a, i) => (
        <div key={i} style={cardStyle}>
          <RowTitle
            name={text(a.google_audience_name)}
            id={text(a.google_audience_id) || text(a.first_party_audience_id)}
            badgeLevel="low"
            badgeText={text(a.audience_type)?.replace(/_/g, ' ')}
          />
          {text(a.rationale) && <Detail>{text(a.rationale)}</Detail>}
        </div>
      ))}
      {pmp.map((p, i) => (
        <div key={i} style={cardStyle}>
          <RowTitle name={text(p.deal_name)} id={text(p.inventory_source_id)} badgeLevel="low" badgeText={text(p.exchange)} />
          {text(p.rationale) && <Detail>{text(p.rationale)}</Detail>}
        </div>
      ))}
      {newLines.map((n, i) => (
        <div key={i} style={cardStyle}>
          <RowTitle name={text(n.proposed_name)} id={text(n.insertion_order_id)} badgeLevel="low" badgeText="new line item" />
          {text(n.targeting) && <Detail>{text(n.targeting)}</Detail>}
          {text(n.rationale) && <Detail>{text(n.rationale)}</Detail>}
        </div>
      ))}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Google Ads — Search and Performance Max                             */
/*                                                                     */
/* The same three questions over different objects. DV360 diagnoses    */
/* line items and paces insertion orders; Search diagnoses ad groups,  */
/* ads and keywords and paces a campaign against its BUDGET; PMax      */
/* diagnoses asset groups. The shapes differ enough that reusing the   */
/* DV360 renderers would render empty sections — which reads as "the   */
/* analysis found nothing" rather than "nothing here renders it".      */
/* ------------------------------------------------------------------ */

/** The id an entry names, whichever of the three entity keys it used. */
const entityId = (row) => text(row.entity_id) || text(row.asset_group_id) || text(row.ad_group_id);
const entityName = (row) => text(row.entity_name) || text(row.asset_group_name) || text(row.ad_group_name);

/** Google's own primary_status reasons, as badges. */
function StatusReasons({ reasons }) {
  const list = (Array.isArray(reasons) ? reasons : []).map(text).filter(Boolean);
  if (!list.length) return null;
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
      {list.map((r, i) => <Badge key={i} level="high">{r.replace(/_/g, ' ')}</Badge>)}
    </div>
  );
}

function GAdsDiagnosticFindings({ details }) {
  const health = (details.campaign_health && typeof details.campaign_health === 'object') ? details.campaign_health : {};
  const serving = (details.campaign_serving && typeof details.campaign_serving === 'object') ? details.campaign_serving : {};
  const measurement = (details.measurement_check && typeof details.measurement_check === 'object') ? details.measurement_check : {};
  const diagnosis = (Array.isArray(details.delivery_diagnosis) ? details.delivery_diagnosis : []).filter(Boolean);
  const issues = (Array.isArray(details.blocking_issues) ? details.blocking_issues : []).filter(Boolean);
  const conflicts = (Array.isArray(details.negative_keyword_conflicts) ? details.negative_keyword_conflicts : []).filter(Boolean);
  const blocked = diagnosis.filter((d) => d.can_deliver === false || String(d.can_deliver).toLowerCase() === 'false');
  if (!text(health.summary) && !diagnosis.length && !issues.length) return null;
  const verdictLevel = { HEALTHY: 'ok', DEGRADED: 'medium', STALLED: 'blocked' }[String(health.verdict || '').toUpperCase()];
  const measurementLevel = { OK: 'ok', AT_RISK: 'medium', BLOCKED: 'blocked' }[String(measurement.verdict || '').toUpperCase()];
  return (
    <Section step="1" title="Diagnosis" subtitle="Whether each entity can actually serve.">
      {(text(health.verdict) || text(health.summary)) && (
        <SummaryBox label="Campaign health">
          {text(health.verdict) && (
            <span style={{ marginRight: '8px' }}><Badge level={verdictLevel}>{text(health.verdict)}</Badge></span>
          )}
          {text(health.summary)}
          {health.ad_groups_blocked != null && (
            <span style={{ color: '#6b7280' }}> ({num(health.ad_groups_blocked)} of {num(health.ad_groups_checked)} ad groups blocked)</span>
          )}
          {health.asset_groups_blocked != null && (
            <span style={{ color: '#6b7280' }}> ({num(health.asset_groups_blocked)} of {num(health.asset_groups_checked)} asset groups blocked)</span>
          )}
        </SummaryBox>
      )}
      {(text(serving.primary_status) || text(serving.evidence)) && (
        <div style={cardStyle}>
          <RowTitle
            name="Campaign serving status"
            badgeLevel={String(serving.status || '').toUpperCase() === 'SERVING' ? 'ok' : 'blocked'}
            badgeText={text(serving.primary_status) || text(serving.status)}
          />
          <StatusReasons reasons={serving.primary_status_reasons} />
          {text(serving.evidence) && <Detail>{text(serving.evidence)}</Detail>}
        </div>
      )}
      {/* Measurement gets its own card: a Smart Bidding campaign with no conversion
          tracking DELIVERS, so it never appears as a blocked entity — and it cannot
          possibly reach the objective it was set. */}
      {(text(measurement.verdict) || text(measurement.detail)) && (
        <div style={cardStyle}>
          <RowTitle
            name="Conversion tracking"
            badgeLevel={measurementLevel}
            badgeText={text(measurement.conversion_tracking_status) || text(measurement.verdict)}
          />
          {text(measurement.detail) && <Detail>{text(measurement.detail)}</Detail>}
        </div>
      )}
      {blocked.map((d, i) => (
        <div key={`${entityId(d) || 'e'}-${i}`} style={cardStyle}>
          <RowTitle
            name={entityName(d)}
            id={entityId(d)}
            badgeLevel="blocked"
            badgeText={text(d.entity_type)?.replace(/_/g, ' ') || 'cannot serve'}
          />
          {(Array.isArray(d.blockers) ? d.blockers : []).map(text).filter((b) => b && b.toLowerCase() !== 'none').length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
              {d.blockers.map(text).filter((b) => b && b.toLowerCase() !== 'none').map((b, bi) => (
                <Badge key={bi} level="high">{b.replace(/_/g, ' ')}</Badge>
              ))}
            </div>
          )}
          <StatusReasons reasons={d.primary_status_reasons} />
          {text(d.ad_strength) && <Detail>Ad strength: {text(d.ad_strength)}</Detail>}
          {text(d.fix) && <Detail>Fix: {text(d.fix)}</Detail>}
          {text(d.evidence) && <Detail>{text(d.evidence)}</Detail>}
        </div>
      ))}
      {issues.map((issue, i) => (
        <div key={i} style={cardStyle}>
          <RowTitle badgeLevel={issue.severity} badgeText={text(issue.severity)} />
          {text(issue.issue) && <Detail>{text(issue.issue)}</Detail>}
          {text(issue.fix) && <Detail>Fix: {text(issue.fix)}</Detail>}
        </div>
      ))}
      {conflicts.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Negative keyword conflicts</h6>
          {conflicts.map((c, i) => (
            <div key={i} style={cardStyle}>
              <RowTitle name={text(c.negative)} badgeLevel="medium" badgeText={text(c.match_type)} />
              {text(c.detail) && <Detail>{text(c.detail)}</Detail>}
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function GAdsPacingFindings({ details }) {
  const summary = text(details.pacing_summary);
  const pacing = (details.campaign_pacing && typeof details.campaign_pacing === 'object') ? details.campaign_pacing : {};
  const constraint = (details.budget_constraint && typeof details.budget_constraint === 'object') ? details.budget_constraint : {};
  const contributors = (Array.isArray(details.spend_contributors) ? details.spend_contributors : []).filter(Boolean);
  const excluded = (Array.isArray(details.excluded_from_pacing) ? details.excluded_from_pacing : []).filter(Boolean);
  if (!summary && !text(pacing.pacing_status) && !contributors.length) return null;
  const currency = text(pacing.currency_code);
  const money = (v) => (v == null ? null : `${currency ? `${currency} ` : ''}${num(v)}`);
  return (
    <Section step="1" title="Pacing" subtitle="The campaign against the budget it was booked to spend.">
      {summary && <SummaryBox label="Pacing">{summary}</SummaryBox>}
      {text(pacing.pacing_status) && (
        <div style={cardStyle}>
          <RowTitle
            name="Campaign pacing"
            badgeLevel={pacing.pacing_status}
            badgeText={text(pacing.pacing_status)?.replace(/_/g, ' ')}
          />
          <Detail>
            {/* A NOT_COMPUTED rollup nulls figures the code could not verify
                (they were model inventions) — render the badge, not blanks. */}
            {pacing.goal_basis !== 'DAILY_BUDGET' && (pacing.delivered_to_date != null || pacing.goal != null) && (
              <>Delivered {money(pacing.delivered_to_date)} of {money(pacing.goal)}</>
            )}
            {pacing.days_remaining != null && <> · {num(pacing.days_remaining)} days left</>}
            {pacing.average_daily_spend != null ? <> · average {money(pacing.average_daily_spend)}/day / target {money(pacing.daily_budget)}/day · {Math.round(pacing.pace_index * 100)}% of daily target</> : pacing.required_daily_spend != null && <> · needs {money(pacing.required_daily_spend)}/day vs {money(pacing.recent_daily_spend)}/day now</>}
            {pacing.projected_final_spend != null && <> · projected {money(pacing.projected_final_spend)}</>}
          </Detail>
          {/* Where the goal came from is not a footnote. A shortfall against a DERIVED
              goal (daily budget x flight days) is a different claim from one against a
              booked media plan, and acting on the two differs. */}
          {text(pacing.goal_basis) && (
            <div style={{ marginTop: '6px' }}>
              <Badge level={String(pacing.goal_basis).toUpperCase() === 'MEDIA_PLAN' ? 'ok' : 'medium'}>
                goal: {text(pacing.goal_basis).replace(/_/g, ' ').toLowerCase()}
              </Badge>
            </div>
          )}
          {text(pacing.goal_caveat) && <Detail>{text(pacing.goal_caveat)}</Detail>}
          {text(pacing.runs_out_on) && <Detail>Budget runs out on {text(pacing.runs_out_on)}.</Detail>}
        </div>
      )}
      {(text(constraint.verdict) || text(constraint.evidence)) && (
        <div style={cardStyle}>
          <RowTitle name="What limits it" badgeLevel="medium" badgeText={text(constraint.verdict)?.replace(/_/g, ' ')} />
          {constraint.days_at_budget_cap != null && (
            <Detail>Hit the daily budget cap on {num(constraint.days_at_budget_cap)} of {num(constraint.days_measured)} days.</Detail>
          )}
          {text(constraint.evidence) && <Detail>{text(constraint.evidence)}</Detail>}
          {text(constraint.impression_share_caveat) && <Detail>{text(constraint.impression_share_caveat)}</Detail>}
        </div>
      )}
      {contributors.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Where the spend is</h6>
          {contributors.map((c, i) => (
            <div key={`${entityId(c) || 'e'}-${i}`} style={cardStyle}>
              <RowTitle
                name={entityName(c)}
                id={entityId(c)}
                badgeLevel="medium"
                badgeText={text(c.cause)?.replace(/_/g, ' ')}
              />
              <Detail>
                {money(c.spend)}
                {text(c.share_of_campaign_spend) && <> · {text(c.share_of_campaign_spend)} of campaign spend</>}
                {c.cost_per_conversion != null && <> · {money(c.cost_per_conversion)} per conversion</>}
              </Detail>
              {text(c.detail) && <Detail>{text(c.detail)}</Detail>}
            </div>
          ))}
        </div>
      )}
      {text(details.concentration_risk) && <div style={quietBox}>{text(details.concentration_risk)}</div>}
      {excluded.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: '0.8em', color: '#6b7280' }}>
          Excluded from pacing: {excluded.map(entityId).filter(Boolean).join(', ')} (handled by the diagnostic run)
        </p>
      )}
    </Section>
  );
}

/** The growth agents' summary block — a different shape from DV360's. */
function GAdsPerformanceFindings({ details }) {
  const summary = (details.performance_summary && typeof details.performance_summary === 'object')
    ? details.performance_summary : {};
  if (!text(summary.headline) && !text(summary.current_state)) return null;
  return (
    <Section step="1" title="Performance" subtitle="Where this campaign's growth is.">
      {text(summary.headline) && <SummaryBox label="Headline">{text(summary.headline)}</SummaryBox>}
      {text(summary.current_state) && <div style={quietBox}>{text(summary.current_state)}</div>}
      {text(summary.primary_constraint) && (
        <div style={cardStyle}>
          <RowTitle name="Primary constraint" badgeLevel="medium" badgeText={text(summary.primary_constraint)?.replace(/_/g, ' ')} />
          {text(summary.constraint_evidence) && <Detail>{text(summary.constraint_evidence)}</Detail>}
        </div>
      )}
      {text(summary.combined_expected_gain) && (
        <div style={cardStyle}>
          <RowTitle name="Combined expected gain" badgeLevel="low" />
          <Detail>{text(summary.combined_expected_gain)}</Detail>
        </div>
      )}
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Social — Facebook and LinkedIn                                      */
/* ------------------------------------------------------------------ */

/*
 * One family for both platforms, because their analyses have one shape: a parent that
 * may hold the budget, children that may hold their own, creatives beneath. Only the
 * NOUNS differ — Facebook writes ad_set_id and campaign_health, LinkedIn writes
 * campaign_id and group_health — so each read below accepts either spelling rather
 * than duplicating three renderers to change a word. Same reason the backend's two
 * platforms share social_focused_optimizer_reasoning.
 */

const socialId = (row) => text(row.ad_set_id) || text(row.campaign_id) || text(row.entity_id);
const socialName = (row) => text(row.ad_set_name) || text(row.campaign_name) || text(row.entity_name);

/** The first of several keys the row actually carries. */
const pick = (row, ...keys) => {
  for (const key of keys) if (row?.[key] != null) return row[key];
  return null;
};

/** Every social recommendation is applied by hand; the analysis says why once. */
function AdvisoryNote({ details }) {
  const note = text(details.apply_support);
  if (!note) return null;
  return (
    <p style={{ margin: '8px 0 0', fontSize: '0.8em', color: '#6b7280', lineHeight: 1.5 }}>
      {note}
    </p>
  );
}

function SocialDiagnosticFindings({ details }) {
  const health = (() => {
    const h = details.campaign_health || details.group_health;
    return h && typeof h === 'object' ? h : {};
  })();
  const diagnosis = (Array.isArray(details.delivery_diagnosis) ? details.delivery_diagnosis : []).filter(Boolean);
  const issues = (Array.isArray(details.blocking_issues) ? details.blocking_issues : []).filter(Boolean);
  if (!text(health.summary) && !diagnosis.length && !issues.length) return null;
  const verdictLevel = { HEALTHY: 'ok', DEGRADED: 'medium', STALLED: 'blocked' }[String(health.verdict || '').toUpperCase()];
  const checked = pick(health, 'ad_sets_checked', 'campaigns_checked');
  const blocked = pick(health, 'ad_sets_blocked', 'campaigns_blocked');
  const noun = health.campaigns_checked != null ? 'campaigns' : 'ad sets';
  return (
    <Section step="1" title="Diagnosis" subtitle="Whether each entity can actually serve.">
      {(text(health.verdict) || text(health.summary)) && (
        <SummaryBox label="Health">
          {text(health.verdict) && (
            <span style={{ marginRight: '8px' }}><Badge level={verdictLevel}>{text(health.verdict)}</Badge></span>
          )}
          {text(health.summary)}
          {blocked != null && (
            <span style={{ color: '#6b7280' }}> ({num(blocked)} of {num(checked)} {noun} blocked)</span>
          )}
        </SummaryBox>
      )}
      {diagnosis.map((d, i) => {
        const blockers = (Array.isArray(d.blockers) ? d.blockers : [])
          .map(text).filter((b) => b && b.toUpperCase() !== 'NONE');
        const canDeliver = d.can_deliver !== false && String(d.can_deliver).toLowerCase() !== 'false';
        return (
          <div key={`${socialId(d) || 'e'}-${i}`} style={cardStyle}>
            <RowTitle
              name={socialName(d)}
              id={socialId(d)}
              badgeLevel={canDeliver ? 'ok' : 'blocked'}
              badgeText={canDeliver ? 'can deliver' : 'blocked'}
            />
            {blockers.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '6px' }}>
                {blockers.map((b, j) => (
                  <Badge key={j} level={String(d.severity).toUpperCase() === 'HARD_BLOCKER' ? 'high' : 'medium'}>
                    {b.replace(/_/g, ' ').toLowerCase()}
                  </Badge>
                ))}
              </div>
            )}
            {text(d.evidence) && <Detail>{text(d.evidence)}</Detail>}
            {d.impressions_in_window != null && (
              <Detail>{num(d.impressions_in_window)} impressions in the window.</Detail>
            )}
            {text(d.fix) && <Detail>Fix: {text(d.fix)}</Detail>}
          </div>
        );
      })}
      {issues.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Blocking issues</h6>
          {issues.map((issue, i) => {
            const named = pick(issue, 'affected_ad_sets', 'affected_campaigns');
            const affected = (Array.isArray(named) ? named : []).map(text).filter(Boolean);
            return (
              <div key={i} style={cardStyle}>
                <RowTitle badgeLevel={issue.severity} badgeText={text(issue.severity)} />
                {text(issue.issue) && <Detail>{text(issue.issue)}</Detail>}
                {affected.length > 0 && <Detail>Affects: {affected.join(', ')}</Detail>}
                {text(issue.fix) && <Detail>Fix: {text(issue.fix)}</Detail>}
              </div>
            );
          })}
        </div>
      )}
      <AdvisoryNote details={details} />
    </Section>
  );
}

function SocialPacingFindings({ details }) {
  const summary = text(details.pacing_summary);
  const structure = text(details.budget_structure);
  const pacing = (Array.isArray(details.budget_pacing) ? details.budget_pacing : []).filter(Boolean);
  const contributors = (Array.isArray(details.shortfall_contributors) ? details.shortfall_contributors : []).filter(Boolean);
  const excluded = (Array.isArray(details.excluded_from_pacing) ? details.excluded_from_pacing : []).filter(Boolean);
  if (!summary && !pacing.length && !contributors.length) return null;
  return (
    <Section step="1" title="Pacing" subtitle="Each budget holder against what it was booked to spend.">
      {summary && <SummaryBox label="Pacing">{summary}</SummaryBox>}
      {/* Which level holds the budget is the fact the rest of the analysis rests on:
          reading a campaign-budget campaign as ad-set-budgeted makes every number
          below it wrong, so it is stated rather than left implicit in the rows. */}
      {structure && (
        <div style={quietBox}><strong>Budget structure:</strong> {structure}</div>
      )}
      {pacing.map((p, i) => {
        const currency = text(p.currency);
        const money = (v) => (v == null ? null : `${currency ? `${currency} ` : ''}${num(v)}`);
        return (
          <div key={`${socialId(p) || 'b'}-${i}`} style={cardStyle}>
            <RowTitle
              name={socialName(p) || (text(p.level) ? `${text(p.level).replace(/_/g, ' ')} budget` : 'Budget')}
              id={socialId(p)}
              badgeLevel={p.pacing_status}
              badgeText={text(p.pacing_status)?.replace(/_/g, ' ')}
            />
            <Detail>
              Spent {money(p.spend_to_date)} of {money(p.budget)}
              {text(p.budget_type) && <> ({text(p.budget_type).toLowerCase()})</>}
              {p.days_remaining != null && <> · {num(p.days_remaining)} days left</>}
              {p.average_daily_spend != null ? <> · average {money(p.average_daily_spend)}/day / target {money(p.daily_budget)}/day · {Math.round(p.pace_index * 100)}% of daily target</> : p.required_daily_spend != null && <> · needs {money(p.required_daily_spend)}/day vs {money(p.recent_daily_spend)}/day now</>}
              {p.projected_final_spend != null && <> · projected {money(p.projected_final_spend)}</>}
            </Detail>
            {text(p.math) && <Detail>{text(p.math)}</Detail>}
          </div>
        );
      })}
      {contributors.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Where the shortfall comes from</h6>
          {contributors.map((c, i) => (
            <div key={`${socialId(c) || 'c'}-${i}`} style={cardStyle}>
              <RowTitle
                name={socialName(c)}
                id={socialId(c)}
                badgeLevel="medium"
                badgeText={text(c.cause)?.replace(/_/g, ' ')}
              />
              {(c.spend != null || c.peer_average_spend != null) && (
                <Detail>
                  Spent {num(c.spend)}
                  {c.peer_average_spend != null && <> vs a peer average of {num(c.peer_average_spend)}</>}
                </Detail>
              )}
              {text(c.detail) && <Detail>{text(c.detail)}</Detail>}
            </div>
          ))}
        </div>
      )}
      {excluded.length > 0 && (
        <p style={{ margin: '8px 0 0', fontSize: '0.8em', color: '#6b7280' }}>
          Excluded from pacing: {excluded.map(socialId).filter(Boolean).join(', ')} (handled by the diagnostic run)
        </p>
      )}
      <AdvisoryNote details={details} />
    </Section>
  );
}

function SocialPerformanceFindings({ details }) {
  const summary = (details.performance_summary && typeof details.performance_summary === 'object')
    ? details.performance_summary : {};
  const rows = (() => {
    const r = details.ad_set_performance || details.campaign_performance;
    return (Array.isArray(r) ? r : []).filter(Boolean);
  })();
  const creatives = (Array.isArray(details.creative_observations) ? details.creative_observations : []).filter(Boolean);
  if (!text(summary.overall) && !rows.length && !creatives.length) return null;
  const ASSESSMENT = {
    STRONG: 'ok', ADEQUATE: 'low', UNDERPERFORMING: 'high', UNDER_SCALED: 'medium',
  };
  return (
    <Section step="1" title="Performance" subtitle="Where this campaign's efficiency and upside are.">
      {text(summary.overall) && <SummaryBox label="Performance">{text(summary.overall)}</SummaryBox>}
      {text(summary.objective) && (
        <div style={quietBox}><strong>Judged against:</strong> {text(summary.objective)}</div>
      )}
      {rows.map((r, i) => (
        <div key={`${socialId(r) || 'p'}-${i}`} style={cardStyle}>
          <RowTitle
            name={socialName(r)}
            id={socialId(r)}
            badgeLevel={ASSESSMENT[String(r.assessment || '').toUpperCase()]}
            badgeText={text(r.assessment)?.replace(/_/g, ' ')}
          />
          <Detail>
            {r.impressions != null && <>{num(r.impressions)} impressions</>}
            {r.spend != null && <> · {num(r.spend)} spend</>}
            {text(r.share_of_spend) && <> ({text(r.share_of_spend)} of campaign)</>}
            {r.ctr != null && <> · {num(r.ctr)} CTR</>}
            {r.cpm != null && <> · {num(r.cpm)} CPM</>}
            {r.cost_per_conversion != null && <> · {num(r.cost_per_conversion)} per conversion</>}
            {r.frequency != null && <> · {num(r.frequency)} frequency</>}
          </Detail>
          {text(r.efficiency_read) && <Detail>{text(r.efficiency_read)}</Detail>}
        </div>
      ))}
      {creatives.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Creative</h6>
          {creatives.map((c, i) => (
            <div key={`${text(c.ad_id) || text(c.creative_id) || 'cr'}-${i}`} style={cardStyle}>
              <RowTitle
                name={text(c.ad_name) || text(c.creative_name)}
                id={text(c.ad_id) || text(c.creative_id)}
                badgeLevel={String(c.action || '').toUpperCase() === 'PAUSE' ? 'high' : 'medium'}
                badgeText={text(c.action)}
              />
              <Detail>
                {c.impressions != null && <>{num(c.impressions)} impressions</>}
                {c.ctr != null && <> · {num(c.ctr)} CTR</>}
              </Detail>
              {text(c.observation) && <Detail>{text(c.observation)}</Detail>}
            </div>
          ))}
        </div>
      )}
      {text(summary.concentration_risk) && <div style={quietBox}>{text(summary.concentration_risk)}</div>}
      <AdvisoryNote details={details} />
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Yahoo DSP                                                           */
/* ------------------------------------------------------------------ */

/*
 * Its own family rather than a fourth platform in `social`, for the same reason the
 * backend gives it its own reasoning module: a Yahoo analysis talks about site lists,
 * audience segments, contextual categories and budget schedules, and its rows are
 * keyed on line_id rather than ad_set_id or campaign_id.
 *
 * Every Yahoo recommendation is advisory — the launcher base stamps that in code
 * because Yahoo has no field-edit executor here — so the cards between these two
 * sections carry no Apply button, and `apply_support` says why once.
 */

const yahooRows = (value) => (Array.isArray(value) ? value : []).filter(Boolean);

/** Shown when a run had no delivery export behind it, so the reader knows why. */
function YahooSettingsOnlyNote({ details }) {
  const available = details.performance_summary?.delivery_data_available
    ?? details.pacing_summary?.delivery_data_available;
  if (available !== false) return null;
  return (
    <SummaryBox label="No delivery reporting">
      This analysis was made from campaign settings alone — no delivery export was
      supplied for the run, so nothing here states impressions, spend or pace.
    </SummaryBox>
  );
}

function YahooDiagnosticFindings({ details }) {
  const summary = details.diagnostic_summary && typeof details.diagnostic_summary === 'object'
    ? details.diagnostic_summary : {};
  const lines = yahooRows(details.line_diagnostics);
  const issues = yahooRows(details.blocking_issues);
  if (!text(summary.overall) && !lines.length && !issues.length) return null;
  return (
    <Section step="1" title="Diagnosis" subtitle="Whether each line can actually serve.">
      {text(summary.overall) && (
        <SummaryBox label="Health">
          {text(summary.overall)}
          {summary.lines_blocked != null && (
            <span style={{ color: '#6b7280' }}>
              {' '}({num(summary.lines_blocked)} of {num(summary.lines_assessed)} lines blocked)
            </span>
          )}
        </SummaryBox>
      )}
      {text(summary.campaign_status_note) && <Detail>{text(summary.campaign_status_note)}</Detail>}
      {lines.map((line, i) => {
        const blockers = yahooRows(line.blockers).map(text).filter(Boolean);
        const throttles = yahooRows(line.throttles).map(text).filter(Boolean);
        const canDeliver = line.can_deliver !== false && String(line.can_deliver).toLowerCase() !== 'false';
        return (
          <div key={`${text(line.line_id) || 'line'}-${i}`} style={cardStyle}>
            <RowTitle
              name={text(line.line_name)}
              id={text(line.line_id)}
              badgeLevel={canDeliver ? 'ok' : 'blocked'}
              badgeText={text(line.assessment)?.replace(/_/g, ' ') || (canDeliver ? 'can deliver' : 'blocked')}
            />
            {line.ads_total != null && (
              <Detail>
                {num(line.ads_able_to_run ?? line.ads_total)} of {num(line.ads_total)} ads can run
                {text(line.flight) && <> · {text(line.flight)}</>}
              </Detail>
            )}
            {blockers.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', margin: '6px 0' }}>
                {blockers.map((b, j) => <Badge key={j} level="high">{b}</Badge>)}
              </div>
            )}
            {throttles.map((t, j) => <Detail key={j}>Throttle: {t}</Detail>)}
          </div>
        );
      })}
      {issues.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Blocking issues</h6>
          {issues.map((issue, i) => (
            <div key={i} style={cardStyle}>
              <RowTitle
                name={text(issue.title)}
                badgeLevel={issue.severity}
                badgeText={text(issue.category)?.replace(/_/g, ' ')}
              />
              {text(issue.rationale) && <Detail>{text(issue.rationale)}</Detail>}
              {text(issue.evidence) && <Detail>{text(issue.evidence)}</Detail>}
              {yahooRows(issue.line_names).length > 0 && (
                <Detail>Affects: {yahooRows(issue.line_names).map(text).filter(Boolean).join(', ')}</Detail>
              )}
            </div>
          ))}
        </div>
      )}
      <AdvisoryNote details={details} />
    </Section>
  );
}

function YahooPacingFindings({ details }) {
  const summary = details.pacing_summary && typeof details.pacing_summary === 'object'
    ? details.pacing_summary : {};
  const schedules = yahooRows(details.schedule_pacing);
  const lines = yahooRows(details.line_pacing);
  const gaps = yahooRows(details.data_gaps).map(text).filter(Boolean);
  if (!text(summary.overall) && !schedules.length && !lines.length) return null;
  return (
    <Section step="1" title="Pacing" subtitle="Each budget schedule against what it was booked to spend.">
      {text(summary.overall) && (
        <SummaryBox label="Pacing">
          {text(summary.overall)}
          {text(summary.budget_unit) && (
            <span style={{ color: '#6b7280' }}> (booked in {text(summary.budget_unit).toLowerCase()})</span>
          )}
        </SummaryBox>
      )}
      <YahooSettingsOnlyNote details={details} />
      {schedules.map((schedule, i) => (
        <div key={`${text(schedule.schedule_name) || 'schedule'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(schedule.schedule_name) || 'Budget schedule'}
            badgeLevel={schedule.pacing_status}
            badgeText={text(schedule.pacing_status)?.replace(/_/g, ' ')}
          />
          <Detail>
            Delivered {num(schedule.delivered_to_date)} of {num(schedule.goal)}
            {schedule.days_remaining != null && <> · {num(schedule.days_remaining)} days left</>}
            {schedule.required_daily_rate != null && (
              <> · needs {num(schedule.required_daily_rate)}/day vs {num(schedule.recent_daily_rate)}/day now</>
            )}
          </Detail>
          {/* A NOT_COMPUTED row without its reason is a dead end — the reason IS the
              finding, exactly as on the DV360 card above. */}
          {String(schedule.pacing_status || '').toUpperCase() === 'NOT_COMPUTED'
            && text(schedule.not_computed_reason) && (
            <Detail>{text(schedule.not_computed_reason)}</Detail>
          )}
          {text(schedule.variance) && <Detail>{text(schedule.variance)}</Detail>}
        </div>
      ))}
      {lines.map((line, i) => (
        <div key={`${text(line.line_id) || 'line'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(line.line_name)}
            id={text(line.line_id)}
            badgeLevel={line.assessment}
            badgeText={text(line.assessment)?.replace(/_/g, ' ')}
          />
          <Detail>
            {text(line.share_of_campaign_delivery) && <>{text(line.share_of_campaign_delivery)} of delivery</>}
            {line.daily_budget != null && <> · daily budget {num(line.daily_budget)}</>}
            {text(line.pacing_mode) && <> · {text(line.pacing_mode)}</>}
            {line.bid_price != null && <> · bid {num(line.bid_price)}</>}
          </Detail>
          {text(line.read) && <Detail>{text(line.read)}</Detail>}
        </div>
      ))}
      {gaps.map((gap, i) => <Detail key={i}>{gap}</Detail>)}
      <AdvisoryNote details={details} />
    </Section>
  );
}

function YahooPerformanceFindings({ details }) {
  const summary = details.performance_summary && typeof details.performance_summary === 'object'
    ? details.performance_summary : {};
  const lines = yahooRows(details.line_performance);
  const creatives = yahooRows(details.creative_observations);
  const gaps = yahooRows(details.data_gaps).map(text).filter(Boolean);
  if (!text(summary.overall) && !lines.length) return null;
  return (
    <Section step="1" title="Performance" subtitle="Each line against the objective it is bought for.">
      {text(summary.overall) && <SummaryBox label="Performance">{text(summary.overall)}</SummaryBox>}
      {/* Which metric the analysis graded on is the fact the rest of it rests on: a
          conversion-goaled line judged on CTR produces recommendations that buy
          clicks instead of the outcome, so it is stated rather than left implicit. */}
      {text(summary.optimizing_toward) && (
        <div style={quietBox}>
          <strong>Optimizing toward:</strong> {text(summary.optimizing_toward)}
          {text(summary.objective_evidence) && <> — {text(summary.objective_evidence)}</>}
        </div>
      )}
      <YahooSettingsOnlyNote details={details} />
      {text(summary.concentration_risk) && <Detail>{text(summary.concentration_risk)}</Detail>}
      {lines.map((line, i) => (
        <div key={`${text(line.line_id) || 'line'}-${i}`} style={cardStyle}>
          <RowTitle
            name={text(line.line_name)}
            id={text(line.line_id)}
            badgeLevel={line.assessment}
            badgeText={text(line.assessment)?.replace(/_/g, ' ')}
          />
          <Detail>
            {text(line.tactic)}
            {text(line.share_of_campaign_delivery) && <> · {text(line.share_of_campaign_delivery)} of delivery</>}
            {line.impressions != null && <> · {num(line.impressions)} impressions</>}
            {line.conversions != null && <> · {num(line.conversions)} conversions</>}
          </Detail>
          {text(line.efficiency_read) && <Detail>{text(line.efficiency_read)}</Detail>}
        </div>
      ))}
      {creatives.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <h6 style={{ margin: '0 0 2px', fontSize: '0.85em', color: '#374151' }}>Creative</h6>
          {creatives.map((creative, i) => (
            <div key={i} style={cardStyle}>
              <RowTitle id={text(creative.ad_id)} badgeText={text(creative.action)} />
              {text(creative.observation) && <Detail>{text(creative.observation)}</Detail>}
            </div>
          ))}
        </div>
      )}
      {gaps.map((gap, i) => <Detail key={i}>{gap}</Detail>)}
      <AdvisoryNote details={details} />
    </Section>
  );
}

/* ------------------------------------------------------------------ */
/* Public sections                                                     */
/* ------------------------------------------------------------------ */

const FINDINGS = {
  dv360: {
    pacing: PacingFindings,
    diagnostic: DiagnosticFindings,
    performance: PerformanceFindings,
  },
  gads: {
    pacing: GAdsPacingFindings,
    diagnostic: GAdsDiagnosticFindings,
    performance: GAdsPerformanceFindings,
  },
  social: {
    pacing: SocialPacingFindings,
    diagnostic: SocialDiagnosticFindings,
    performance: SocialPerformanceFindings,
  },
  yahoo: {
    pacing: YahooPacingFindings,
    diagnostic: YahooDiagnosticFindings,
    performance: YahooPerformanceFindings,
  },
};

/**
 * Which renderer family a result belongs to, from the envelope's `platform`.
 *
 * DV360 is the fallback rather than an explicit match because it was the only family
 * for a while and every non-focused result already lands there. A platform whose
 * shape is NOT DV360's must be named here — falling through renders its findings
 * against the wrong keys, which shows as an empty section rather than an error.
 */
function findingsFamily(platform) {
  const value = String(platform || '').toLowerCase();
  if (value === 'search' || value === 'pmax' || isDemandGen(value)) return 'gads';
  if (value === 'fb' || value === 'facebook' || value === 'linkedin') return 'social';
  if (value === 'yahoo' || value === 'yahoo_dsp' || value === 'yahoo-dsp') return 'yahoo';
  return 'dv360';
}

function isDemandGen(platform) {
  return ['demandgen', 'demand-gen', 'demand_gen', 'demand gen',
    'google-ads-demandgen', 'google_demandgen', 'g-ads-demandgen'].includes(String(platform || '').toLowerCase());
}

function DemandGenExtras({ details }) {
  const audiences = Array.isArray(details.audience_recommendations) ? details.audience_recommendations : [];
  const creatives = Array.isArray(details.creative_analysis) ? details.creative_analysis : [];
  const experiments = Array.isArray(details.experiments) ? details.experiments
    : (Array.isArray(details.actionable_suggestions) ? details.actionable_suggestions : [])
      .filter((s) => s?.action_type === 'experiment').map((s) => s.recommended_value);
  const gaps = Array.isArray(details.evidence_gaps) ? details.evidence_gaps : [];
  return <>
    {gaps.length > 0 && <Section title="Evidence gaps">
      {gaps.filter(Boolean).map((gap, i) => <Detail key={i}>
        {text(gap.source)?.replace(/_/g, ' ')}: {text(gap.detail)}
      </Detail>)}
    </Section>}
    {audiences.length > 0 && <Section title="Audience opportunities">
      {audiences.filter(Boolean).map((audience, i) => <div key={i} style={cardStyle}>
        <RowTitle name={text(audience.ad_group_name) || 'Audience review'} id={text(audience.ad_group_id)} />
        {(Array.isArray(audience.segments) ? audience.segments : []).filter(Boolean).map((segment, j) =>
          <Detail key={j}>{[text(segment.audience), text(segment.why)].filter(Boolean).join(' — ')}</Detail>)}
        {text(audience.how_to_apply) && <Detail>{text(audience.how_to_apply)}</Detail>}
      </div>)}
    </Section>}
    {creatives.length > 0 && <Section title="Creative opportunities">
      {creatives.filter(Boolean).map((creative, i) => <div key={i} style={cardStyle}>
        <RowTitle name={text(creative.ad_format)?.replace(/_/g, ' ') || 'Creative review'} id={text(creative.ad_id)} />
        {text(creative.performance_summary) && <Detail>{text(creative.performance_summary)}</Detail>}
        {(Array.isArray(creative.recommendations) ? creative.recommendations : []).map((recommendation, j) =>
          <Detail key={j}>{text(recommendation)}</Detail>)}
      </div>)}
    </Section>}
    {experiments.length > 0 && <Section title="Proposed experiments">
      {experiments.filter(Boolean).map((experiment, i) => <div key={i} style={cardStyle}>
        <RowTitle name={text(experiment.hypothesis) || 'Experiment'} />
        {['variable', 'control', 'treatment', 'success_metric', 'readout_condition', 'stop_condition'].map((key) =>
          text(experiment[key]) && <Detail key={key}>{key.replace(/_/g, ' ')}: {text(experiment[key])}</Detail>)}
      </div>)}
    </Section>}
  </>;
}

/**
 * The object the findings renderers read.
 *
 * The Google Ads launchers nest their answer under `details.analysis` — the key the
 * app's Google Ads suggestion renderer already reads suggestions from, so a focused
 * result renders and applies exactly like a performance one. DV360's sits at the top
 * level. Unwrapped here rather than at each call site, so a caller passes
 * `message.json_response.details` whichever platform produced it.
 */
function findingsDetails(details, family) {
  if (family !== 'gads') return details;
  const analysis = details && typeof details === 'object' ? details.analysis : null;
  return (analysis && typeof analysis === 'object') ? analysis : details;
}

/**
 * The first candidate that is real PROSE — a non-empty string.
 *
 * `text()` renders an object as its JSON, which is right for a value that slipped
 * into a field expecting a scalar and wrong for a verdict: DV360's diagnostic brief
 * puts its closing read in `diagnostic_summary` as a string, while the Yahoo brief
 * makes that key an object of counts and carries the read in `diagnostic_verdict`.
 * Reading the object would print a line of JSON in the verdict box, so the candidates
 * are tried in order and anything that is not a string is skipped rather than
 * stringified.
 */
function prose(candidates) {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
  }
  return null;
}

/** Section 1: what the focused analysis found, rendered above the cards. */
export function FocusedFindingsSection({ kind, details, platform }) {
  if (!details || typeof details !== 'object') return null;
  if (String(platform || '').toLowerCase() === 'ttd') {
    return <Section title="TTD analysis findings">
      {text(details.campaign_analysis?.overall_performance) && <SummaryBox>{text(details.campaign_analysis.overall_performance)}</SummaryBox>}
      {(Array.isArray(details.findings) ? details.findings : []).filter(Boolean).map((finding, index) =>
        <div key={index} style={cardStyle}>
          <RowTitle name={text(finding.ad_group_name)} id={text(finding.ad_group_id)} />
          <Detail>{text(finding.observation)}</Detail>
        </div>)}
      {(Array.isArray(details.data_gaps) ? details.data_gaps : []).map((gap, index) => <Detail key={index}>{text(gap)}</Detail>)}
    </Section>;
  }
  const family = findingsFamily(platform);
  const own = findingsDetails(details, family);
  if (family === 'gads' && own?.campaign_readout?.version === 1) {
    return <CampaignHealthReadout readout={own.campaign_readout} />;
  }
  const Findings = FINDINGS[family][kind];
  return Findings ? <Findings details={findingsDetails(details, family)} /> : null;
}

/** Section 3: extras + the analysis's own closing verdict, below the cards. */
export function FocusedVerdictSection({ kind, details, platform }) {
  if (!details || typeof details !== 'object') return null;
  if (String(platform || '').toLowerCase() === 'ttd') {
    return text(details.optimization_summary)
      ? <Section title="Conclusion"><Detail>{text(details.optimization_summary)}</Detail></Section> : null;
  }
  const family = findingsFamily(platform);
  const own = findingsDetails(details, family);
  // The health readout already summarizes concerns above the cards.
  if (family === 'gads' && own?.campaign_readout?.version === 1) return null;
  const verdict = prose(
    kind === 'pacing' ? [own.pacing_verdict]
      : kind === 'performance' ? [own.performance_verdict, own.summary?.next_steps]
        : [own.diagnostic_verdict, own.diagnostic_summary],
  );
  return (
    <>
      {/* DV360 alone has audience / PMP / new-line-item proposals as separate blocks;
          the Google Ads agents express every one of those as a suggestion card. */}
      {family === 'dv360' && kind === 'performance' && <PerformanceExtras details={own} />}
      {isDemandGen(platform) && kind === 'performance' && <DemandGenExtras details={own} />}
      {verdict && (
        <div style={{
          padding: '12px 15px', background: '#f8f9ff', borderRadius: '6px',
          marginTop: '14px', fontSize: '0.85em', lineHeight: 1.5, color: '#374151',
        }}>
          <strong style={{ display: 'block', marginBottom: '4px' }}>Verdict</strong>
          {verdict}
        </div>
      )}
    </>
  );
}

export default {
  FOCUSED_ANALYSIS_LABELS,
  focusedAnalysisKind,
  FocusedFindingsSection,
  FocusedVerdictSection,
};
